import * as React from 'react'
import { Product } from '@/src/types/api-types'
import { ProductsBasket } from '@/type/basket'
import ProductQtyRow, { PriceOption } from './ProductQtyRow'
import useProduct from '@/lib/hook/useProduct'
import clsx from 'clsx'
import COLORS from '@/src/colors'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { defaultFormatPrice } from './ProductQtyInput'
import PREMIUM from '@/src/PREMIUM'
import generatePriceOptions from '@/lib/products/generatePriceOptions'
import { havePremiumActive } from '@/lib/subscriptions'
import ProfileContext from '../Context/ProfileContext'
import { useContext } from 'react'

export type ProductQtyDropdownClasses = {
  panel?: string
  footer?: string
}

interface Props {
  product: Product | ProductsBasket
  discountDisplay?: boolean
  onChange?: (qty: number) => void
  initialQty?: number
  onClose: () => void
  maxHeight?: number | string
  confirmLabel?: string
  classes?: ProductQtyDropdownClasses
  currentQty?: number
  width?: number | string
  onClickPremium?: () => void
  compact?: boolean
}

const ProductQtyDropdown: React.FC<Props> = ({
  product,
  onChange,
  initialQty = 0,
  onClose,
  classes,
  currentQty,
  width,
  onClickPremium,
  compact = false,
}) => {
  const { profile } = useContext(ProfileContext)
  // dans certains cas nous n'avons pas les prix
  // appels à useProduct pour avec les prix
  const slug = (product as any)?.seo_name as string | undefined
  const productIdStr = product?.product_id ? String(product.product_id) : undefined
  const skeyProduct = (product as any)?.skeyProduct as string | undefined
  const { product: fetchedProduct } = useProduct(slug, productIdStr, skeyProduct)

  const effectiveProduct: any = fetchedProduct ?? product

  const stepPriceOptions = generatePriceOptions(effectiveProduct)

  // Quantité sélectionnée contrôlée par le parent
  const selectedQty = currentQty ?? initialQty ?? stepPriceOptions[0]?.quantity ?? 0

  // Index du meilleur prix (premier match en cas d'egalite) — evite multi-badges si quantites dupliquees
  const bestIndex = stepPriceOptions.reduce(
    (bestIdx, o, idx) => (o.finalPrice < stepPriceOptions[bestIdx].finalPrice ? idx : bestIdx),
    0
  )
  const best = stepPriceOptions[bestIndex] ?? stepPriceOptions[0]

  // Abonnement (Pharmedi+) — présent uniquement si prices_current_subscription est fourni côté produit
  const premium: PriceOption | undefined = (() => {
    const subs = effectiveProduct?.prices_current_subscription

    if (!subs || subs.length === 0) return undefined

    const mapped = subs.map((price: any) => {
      const mini = typeof price.mini === 'string' ? parseFloat(price.mini) : price.mini || 0
      const maxi = typeof price.maxi === 'string' ? parseFloat(price.maxi) : price.maxi || 0
      const cataloguePrice =
        typeof price.catalogue_price === 'string'
          ? parseFloat(price.catalogue_price)
          : price.catalogue_price || 0
      const priceSales =
        typeof price.price_sales === 'string'
          ? parseFloat(price.price_sales)
          : price.price_sales || 0
      const percentDiscount =
        typeof price.percent_discount === 'string'
          ? parseFloat(price.percent_discount)
          : price.percent_discount || 0

      return {
        quantity: mini,
        maxQuantity: maxi,
        originalPrice: cataloguePrice,
        discount: percentDiscount,
        finalPrice: priceSales,
      } as PriceOption
    })

    // Choisir l'option d'abonnement la plus avantageuse (prix final le plus bas)
    return mapped.reduce((b, o) => (o.finalPrice < b.finalPrice ? o : b), mapped[0])
  })()

  const getOptionForQty = (qty: number): PriceOption => {
    // Cherche une tranche où qty est entre [quantity, maxQuantity]
    const found = stepPriceOptions.find(o => {
      const min = Number(o.quantity)
      //@ts-ignore
      const max = typeof o.maxQuantity === 'number' && o.maxQuantity > 0 ? o.maxQuantity : Infinity
      return qty >= min && qty <= max
    })
    if (found) return found
    // Sinon, renvoie la plus grande tranche dont min <= qty, sinon la première
    const candidates = stepPriceOptions.filter(o => qty >= o.quantity)
    if (candidates.length > 0) return candidates[candidates.length - 1]
    return stepPriceOptions[0]
  }

  const currentForSelected = getOptionForQty(selectedQty || (best as PriceOption).quantity)

  const select = (o: PriceOption) =>
    onChange?.(
      o.quantity < effectiveProduct?.coeffQuantity ? effectiveProduct?.coeffQuantity : o.quantity
    )

  // Gestion ESC (fermeture sur Escape)
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      id="qty-dropdown-panel"
      className={clsx([
        'fixed z-[11000] shadow-4xl bg-white border border-solid rounded-md',
        `border-[${COLORS.border3}]`,
        compact ? 'px-2 py-1 text-[0.7rem]' : 'px-4 py-2 text-[0.85rem]',
        classes?.panel,
      ])}
      style={{ width: width || '100%' }}
    >
      {stepPriceOptions.length > 0 ? (
        <>
          {/* 4) Liste des paliers de prix dégressifs */}
          <div className={compact ? 'space-y-2' : 'space-y-4'}>
            {stepPriceOptions.map((opt, index) => {
              return (
                <ProductQtyRow
                  key={index}
                  opt={opt}
                  onClick={() => select(opt)}
                  compact={compact}
                  rightSlot={
                    <div className="flex items-center justify-between">
                      <div
                        className={clsx(
                          'text-[#949696] text-left',
                          compact && 'text-[10px]'
                        )}
                      >
                        x
                        {opt.quantity < effectiveProduct?.coeffQuantity
                          ? effectiveProduct?.coeffQuantity
                          : opt.quantity}
                      </div>
                      {currentForSelected.quantity === opt.quantity && (
                        <CheckCircleIcon
                          className="text-[#213390]"
                          fontSize={compact ? 'small' : 'medium'}
                        />
                      )}
                    </div>
                  }
                  showBadge={index === bestIndex}
                  showBorder={index === bestIndex}
                  classes={{
                    container:
                      index === bestIndex
                        ? compact
                          ? '-mx-1 !w-[calc(100%+8px)]'
                          : '-mx-2 !w-[calc(100%+16px)]'
                        : '',
                  }}
                />
              )
            })}
          </div>

          {premium && premium.finalPrice < best.finalPrice && !havePremiumActive(profile) && (
            <>
              <div
                className={clsx(
                  'flex justify-between items-center rounded-md bg-[#FFF9EA] cursor-pointer',
                  compact ? 'p-1 -mx-1 mt-1' : 'p-2 -mx-2 mt-2'
                )}
              >
                <div
                  className={clsx(
                    'text-white bg-[linear-gradient(90deg,#FF9214_0%,#FFCF6A_100%)] rounded-lg',
                    compact ? 'text-[9px] px-1 py-0.5' : 'text-[0.6rem] p-2'
                  )}
                >
                  Avec {PREMIUM.name}
                </div>
                <div className="text-center">
                  <div
                    className={clsx(
                      'text-white bg-[#FFB119] rounded-lg whitespace-nowrap',
                      compact ? 'px-1 py-0.5 text-[10px]' : 'px-2 py-1'
                    )}
                  >
                    {Math.round(premium.discount)}%
                  </div>
                </div>
                <div
                  className={clsx(
                    'text-center text-[#FFB119] font-bold',
                    compact && 'text-xs'
                  )}
                >
                  {defaultFormatPrice(premium.finalPrice)}
                </div>
                <div
                  className={clsx(
                    'text-left text-[#FFB103] underline font-semibold',
                    compact ? 'w-16 text-[10px]' : 'w-24'
                  )}
                  onClick={() => onClickPremium?.()}
                >
                  Je découvre
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="px-4 py-3 text-sm text-slate-600">Prix indisponibles pour ce produit.</div>
      )}
    </div>
  )
}

export default ProductQtyDropdown
